import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Search, ShieldCheck, Link2, Eraser } from "lucide-react";

const Dato = ({ etiqueta, valor, testid }) => (
  <div className="rounded-xl border border-border px-4 py-3">
    <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{etiqueta}</p>
    <p className="font-display text-2xl font-extrabold mt-1.5" data-testid={testid}>{valor}</p>
  </div>
);

/** Auditoría de datos históricos: sesiones sueltas, turnos sin hora y aforos sin definir. */
export const PendientesRevisar = ({ clases = [], sedeId = "", onCambio }) => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [aplicando, setAplicando] = useState(false);

  const auditar = async (aplicar) => {
    const marcar = aplicar ? setAplicando : setCargando;
    marcar(true);
    try {
      const { data } = await api.post("/mantenimiento/auditoria", {
        aplicar, sede_id: sedeId === "todas" ? "" : sedeId,
      });
      setDatos(data);
      toast.success(aplicar
        ? `Cambios aplicados · ${data.sesiones.revinculadas} sesión(es) re-vinculadas · ${data.aforos.turnos_corregidos} aforo(s) corregidos`
        : "Revisión terminada: nada se ha modificado");
      if (aplicar) onCambio?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      marcar(false);
    }
  };

  const revincular = async (sesionId, claseId) => {
    try {
      await api.post(`/mantenimiento/sesiones/${sesionId}/revincular`, { clase_id: claseId });
      toast.success("Sesión re-vinculada a su clase");
      await auditar(false);
      onCambio?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    }
  };

  const descartarVacias = async () => {
    const n = datos?.sesiones?.sin_rastro ?? 0;
    if (!window.confirm(
      `Se quitarán ${n} sesión(es) sueltas que no guardan ningún dato: ni lista pasada ni reposiciones.\n\n`
      + "Quedan copiadas por si hiciera falta recuperarlas. Las que tienen historial no se tocan. ¿Continuamos?"
    )) return;
    setAplicando(true);
    try {
      const { data } = await api.post("/mantenimiento/sesiones/vacias/descartar", {
        aplicar: true, sede_id: sedeId === "todas" ? "" : sedeId,
      });
      toast.success(`${data.descartadas} sesión(es) vacías retiradas`
        + (data.conservadas_con_historial ? ` · ${data.conservadas_con_historial} con historial conservadas` : ""));
      onCambio?.();
      await auditar(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setAplicando(false);
    }
  };

  const confirmarAplicar = () => {
    const n = datos?.sesiones?.revinculadas ?? 0;
    const a = datos?.aforos?.clases_afectadas ?? 0;
    if (!window.confirm(
      `Se re-vincularán ${n} sesión(es) con destino inequívoco y se escribirá el aforo de ${a} clase(s).\n\n`
      + "Antes se guarda una copia de seguridad completa. Nada se borra. ¿Continuamos?")) return;
    auditar(true);
  };

  const claseNombre = (id) => clases.find((c) => c.id === id)?.nombre || id;
  const revision = datos?.sesiones?.detalle_revision || [];
  const sinHora = datos?.turnos_sin_hora?.detalle || [];
  const aforos = datos?.aforos?.detalle || [];

  return (
    <div className="mt-10" data-testid="pendientes-revisar">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-2xl font-extrabold">Pendientes de revisar</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Datos históricos que no cuadran: sesiones sin clase, turnos sin hora y aforos sin definir.
            La revisión no modifica nada.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full font-semibold" onClick={() => auditar(false)}
            disabled={cargando || aplicando} data-testid="auditar-btn">
            {cargando ? <Loader2 className="h-4 w-4 animate-spin" />
              : <><Search className="h-4 w-4 mr-2" /> Revisar datos</>}
          </Button>
          <Button className="rounded-full font-semibold" onClick={confirmarAplicar}
            disabled={!datos || cargando || aplicando} data-testid="aplicar-auditoria-btn">
            {aplicando ? <Loader2 className="h-4 w-4 animate-spin" />
              : <><ShieldCheck className="h-4 w-4 mr-2" /> Aplicar cambios seguros</>}
          </Button>
        </div>
      </div>

      {!datos ? (
        <Card className="rounded-2xl p-6 border-border shadow-sm">
          <p className="text-sm text-muted-foreground" data-testid="pendientes-sin-revisar">
            Pulsa «Revisar datos» para ver el informe. Solo lee: no cambia nada hasta que lo apliques.
          </p>
        </Card>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Dato etiqueta="Sesiones sin clase" valor={datos.sesiones.huerfanas}
              testid="pend-huerfanas" />
            <Dato etiqueta="Re-vinculables sin dudas" valor={datos.sesiones.revinculadas}
              testid="pend-revinculables" />
            <Dato etiqueta="Decides tú" valor={datos.sesiones.pendientes_de_revisar}
              testid="pend-revision" />
            <Dato etiqueta="Vacías, sin rastro" valor={datos.sesiones.sin_rastro ?? 0}
              testid="pend-sin-rastro" />
            <Dato etiqueta="Turnos sin hora" valor={datos.turnos_sin_hora.total}
              testid="pend-sin-hora" />
          </div>

          {(datos.sesiones.sin_rastro ?? 0) > 0 && (
            <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="pend-vacias">
              <h3 className="font-display text-lg font-bold mb-1">
                {datos.sesiones.sin_rastro} sesión(es) sueltas que no guardan nada
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Su clase ya no existe y no tienen ni lista pasada ni reposiciones: son ruido de
                clases borradas. Al retirarlas quedan copiadas por si hiciera falta recuperarlas.
              </p>
              <Button variant="outline" className="rounded-full font-semibold text-destructive"
                onClick={descartarVacias} disabled={aplicando || cargando}
                data-testid="descartar-vacias-btn">
                {aplicando ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <><Eraser className="h-4 w-4 mr-2" /> Retirar las vacías</>}
              </Button>
            </Card>
          )}

          {datos.copia_de_seguridad && (
            <p className="text-xs text-muted-foreground" data-testid="pend-copia">
              Copia de seguridad guardada con el sufijo <b>{datos.copia_de_seguridad.sufijo}</b>.
            </p>
          )}

          {revision.length > 0 && (
            <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="pend-lista-revision">
              <h3 className="font-display text-lg font-bold mb-1">Sesiones que debes asignar a mano</h3>
              <p className="text-sm text-muted-foreground mb-5">
                Su clase ya no existe pero conservan lista pasada o reposiciones, así que no se borran:
                elige a qué clase pertenecen.
                {datos.sesiones.pendientes_de_revisar > revision.length
                  && ` Se muestran las primeras ${revision.length} de ${datos.sesiones.pendientes_de_revisar}.`}
              </p>
              <div className="space-y-3">
                {revision.map((s) => (
                  <div key={s.sesion_id} className="rounded-xl border border-border p-4"
                    data-testid={`pend-sesion-${s.sesion_id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="rounded-full">{s.fecha} · {s.hora_inicio}</Badge>
                      <Badge variant="outline" className="rounded-full">{s.asistencias} con lista</Badge>
                      {s.reposiciones > 0 && (
                        <Badge variant="outline" className="rounded-full">{s.reposiciones} reposición(es)</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">{s.motivo}</p>
                    {s.alumnos?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">Alumnos: {s.alumnos.join(", ")}</p>
                    )}
                    <Select onValueChange={(v) => revincular(s.sesion_id, v)}>
                      <SelectTrigger className="mt-3 rounded-lg" data-testid={`revincular-${s.sesion_id}`}>
                        <SelectValue placeholder="Asignar a una clase" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[50vh]">
                        {(s.candidatas?.length
                          ? s.candidatas.map((id) => ({ id, nombre: claseNombre(id) }))
                          : clases.filter((c) => c.sede_id === s.sede_id)
                        ).map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {sinHora.length > 0 && (
            <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="pend-lista-sin-hora">
              <h3 className="font-display text-lg font-bold mb-1 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" /> Alumnos sin hora confirmada
              </h3>
              <p className="text-sm text-muted-foreground mb-5">
                Están matriculados en la clase pero no en un turno concreto. No se les inventa la hora:
                entra en su ficha → «Cambiar día fijo» y elige el turno real.
              </p>
              <div className="space-y-2">
                {sinHora.map((t, i) => (
                  <div key={`${t.alumno_id}-${t.clase_id}-${i}`}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-muted px-4 py-3"
                    data-testid={`pend-turno-${t.alumno_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{t.alumno} · {t.matricula}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {t.clase} · {t.motivo}
                        {t.dias_sin_hora?.length ? ` (${t.dias_sin_hora.join(", ")})` : ""}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Turnos posibles: {t.turnos_posibles?.join(" · ") || "—"}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" className="rounded-full shrink-0"
                      onClick={() => window.open(`/admin/alumnos/${t.alumno_id}`, "_blank")}
                      data-testid={`abrir-alumno-${t.alumno_id}`}>
                      <Link2 className="h-3.5 w-3.5 mr-1.5" /> Abrir ficha
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {aforos.length > 0 && (
            <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="pend-lista-aforos">
              <h3 className="font-display text-lg font-bold mb-1">Turnos sin aforo propio</h3>
              <p className="text-sm text-muted-foreground mb-5">
                Al aplicar se les escribe el aforo de su clase para que deje de verse un 0.
              </p>
              <div className="space-y-2">
                {aforos.map((a) => (
                  <div key={a.clase_id} className="rounded-xl bg-muted px-4 py-3 text-sm"
                    data-testid={`pend-aforo-${a.clase_id}`}>
                    <span className="font-semibold">{a.clase}</span>
                    <span className="text-muted-foreground"> → {a.capacidad_clase} plazas en {a.turnos_sin_aforo.join(", ")}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {revision.length === 0 && sinHora.length === 0 && aforos.length === 0
            && (datos.sesiones.sin_rastro ?? 0) === 0 && (
            <Card className="rounded-2xl p-6 border-border shadow-sm">
              <p className="text-sm text-muted-foreground" data-testid="pendientes-todo-ok">
                No hay nada pendiente de revisar. Los datos están correctos.
              </p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
